/*
 * Organic 2 Day - homepage enhancements.
 *
 *   1. Background video behind the hero and the "Shop by Category" section.
 *   2. Static background class for "Everyday Essentials" (CSS does the rest).
 *   3. Scroll progress bar, for browsers without scroll-driven CSS animation.
 *   4. Count-up on the trust bar figures.
 *
 * Video is never on the critical path: each clip is fetched only once its
 * section approaches the viewport, and skipped entirely on small screens,
 * data-saver connections, and when reduced motion is preferred. The poster
 * image, set in CSS or by the theme, is shown in those cases.
 */
(function () {
  'use strict';

  var CDN = 'https://cdn.shopify.com/s/files/1/1025/0718/4493/files/';

  var TARGETS = [
    {
      // Hero is the first image-banner on the page.
      find: function () {
        return document.querySelector('[id$="__hero"]');
      },
      cls: 'o2d-hero',
      video: CDN + 'o2d-hero.mp4',
      videoCls: 'o2d-hero__video',
      // The video sits inside the media wrapper so the theme overlay stays on top.
      mount: function (section) {
        return section.querySelector('.banner__media') || section;
      }
    },
    {
      find: function () {
        return document.querySelector('[id$="__categories"]');
      },
      cls: 'o2d-video-bg',
      video: CDN + 'o2d-category-bg.mp4',
      videoCls: 'o2d-video-bg__media',
      mount: function (section) {
        return section;
      }
    }
  ];

  function heavyMediaUnwanted() {
    var mq = window.matchMedia;
    if (mq && mq('(max-width: 749px)').matches) return true;
    if (mq && mq('(prefers-reduced-motion: reduce)').matches) return true;
    var c = navigator.connection;
    if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) return true;
    return false;
  }

  function addVideo(target, section) {
    var mount = target.mount(section);
    if (!mount || mount.querySelector('.' + target.videoCls)) return;

    var v = document.createElement('video');
    v.className = target.videoCls;
    v.muted = true;
    v.defaultMuted = true;
    v.loop = true;
    v.autoplay = true;
    v.preload = 'auto';
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('aria-hidden', 'true');
    v.tabIndex = -1;
    v.src = target.video;

    mount.appendChild(v);

    var p = v.play();
    // Autoplay may still be refused; the poster image remains underneath.
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function lazyVideo(target) {
    var section = target.find();
    if (!section) return;

    section.classList.add(target.cls);
    if (heavyMediaUnwanted()) return;

    if (!('IntersectionObserver' in window)) {
      addVideo(target, section);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            io.disconnect();
            addVideo(target, section);
            return;
          }
        }
      },
      { rootMargin: '400px 0px' }
    );
    io.observe(section);
  }

  function staticBackgrounds() {
    var essentials = document.querySelector('[id$="__subcats"]');
    if (essentials) essentials.classList.add('o2d-image-bg');

    var trust = document.querySelector('[id$="__trust"]');
    if (trust) trust.classList.add('o2d-trust');
  }

  /* Scroll progress. CSS drives this where scroll-timeline is supported;
     this is the fallback for everything else. */
  function progressBar() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var bar = document.createElement('div');
    bar.className = 'o2d-progress';
    document.body.appendChild(bar);

    if (CSS && CSS.supports && CSS.supports('animation-timeline: scroll()')) return;

    var ticking = false;
    function update() {
      var doc = document.documentElement;
      var max = doc.scrollHeight - doc.clientHeight;
      var pct = max > 0 ? doc.scrollTop / max : 0;
      bar.style.transform = 'scaleX(' + pct + ')';
      ticking = false;
    }
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  /* Count the trust-bar figures up when they first scroll into view. */
  function countUp() {
    var trust = document.querySelector('.o2d-trust');
    if (!trust || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var heads = trust.querySelectorAll('h3');
    var items = [];
    for (var i = 0; i < heads.length; i++) {
      var m = heads[i].textContent.match(/^(\D*)(\d[\d,]*)(.*)$/);
      if (m) items.push({ el: heads[i], pre: m[1], target: +m[2].replace(/,/g, ''), post: m[3] });
    }
    if (!items.length) return;

    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          io.disconnect();
          var start = null;
          var DUR = 1100;
          (function step(ts) {
            if (start === null) start = ts;
            var t = Math.min(1, (ts - start) / DUR);
            var eased = 1 - Math.pow(1 - t, 3);
            items.forEach(function (it) {
              var n = Math.round(it.target * eased);
              it.el.textContent = it.pre + n.toLocaleString('en-IN') + it.post;
            });
            if (t < 1) window.requestAnimationFrame(step);
          })(performance.now());
          return;
        }
      },
      { threshold: 0.4 }
    );
    io.observe(trust);
  }

  function init() {
    staticBackgrounds();
    TARGETS.forEach(lazyVideo);
    progressBar();
    countUp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   Persistent cart / checkout control.

   Adding an item halfway down a long category page used to mean
   scrolling back to the header to reach the cart. This keeps a
   live cart control on screen at all times once the cart has
   something in it.

   Desktop: floating pill, bottom right, stacked ABOVE the
   Rewards widget that already sits in that corner.
   Mobile:  full-width bar pinned to the bottom of the viewport.

   Cart state is read from /cart.js and refreshed whenever the
   page makes any /cart/ request, which covers the theme's
   quick-add, the product form and the cart drawer alike.
   ============================================================ */
(function () {
  'use strict';

  var bar, countEl, totalEl;
  var money = function (cents) {
    var n = (cents || 0) / 100;
    return 'Rs ' + n.toLocaleString('en-IN', {
      minimumFractionDigits: n % 1 ? 2 : 0,
      maximumFractionDigits: 2
    });
  };

  function build() {
    bar = document.createElement('div');
    bar.className = 'o2d-cartbar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'Cart summary');
    bar.innerHTML =
      '<button type="button" class="o2d-cartbar__view" aria-label="View cart">' +
      '<span class="o2d-cartbar__icon" aria-hidden="true"></span>' +
      '<span class="o2d-cartbar__meta">' +
      '<span class="o2d-cartbar__count"></span>' +
      '<span class="o2d-cartbar__total"></span>' +
      '</span></button>' +
      '<a class="o2d-cartbar__checkout" href="/checkout">Checkout</a>';

    document.body.appendChild(bar);
    countEl = bar.querySelector('.o2d-cartbar__count');
    totalEl = bar.querySelector('.o2d-cartbar__total');

    bar.querySelector('.o2d-cartbar__view').addEventListener('click', openCart);
  }

  /* Prefer the theme's own drawer so the experience stays native;
     fall back to the cart page if the drawer is not present. */
  function openCart() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    var iconBubble = document.querySelector('#cart-icon-bubble');
    if (drawer && iconBubble) {
      iconBubble.click();
      return;
    }
    window.location.href = '/cart';
  }

  function render(cart) {
    if (!cart) return;
    var n = cart.item_count || 0;
    if (!n) {
      bar.classList.remove('is-visible');
      document.body.classList.remove('o2d-has-cartbar');
      return;
    }
    countEl.textContent = n + (n === 1 ? ' item' : ' items');
    totalEl.textContent = money(cart.total_price);
    bar.classList.add('is-visible');
    document.body.classList.add('o2d-has-cartbar');
  }

  function refresh() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(render)
      .catch(function () {});
  }

  /* Any /cart/ write (add, change, update) should refresh the bar.
     Wrapping fetch covers the theme's own AJAX without needing to
     know which event names this theme version publishes. */
  function watchCartRequests() {
    if (!window.fetch) return;
    var native = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = '';
      try {
        url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      } catch (e) {}
      var p = native.apply(this, args);
      if (/\/cart\//.test(url)) {
        p.then(function () { setTimeout(refresh, 120); }).catch(function () {});
      }
      return p;
    };

    // The theme also posts forms directly in some paths.
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (f && f.action && /\/cart\//.test(f.action)) setTimeout(refresh, 500);
    }, true);
  }

  function init() {
    build();
    watchCartRequests();
    refresh();
    // Cart can change in another tab; re-check when this one regains focus.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) refresh();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
